import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    NgZone,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AppService } from '../../app.service';
import { ReportsService } from '../../services/reports/reports.service';
import { saveAs } from 'file-saver';
import { forkJoin } from 'rxjs';
import { ReferenceDataService } from '../../services/referencedata/reference-data.service';
import { ModalDialogSeverity } from '../../enums/modal-dialog-severity';
import { isArrayWithData } from '../../utilities/array.helpers';

@Component({
    selector: 'app-payments-report',
    templateUrl: './payments-report.component.html',
    styleUrl: './payments-report.component.css',
})
export class PaymentsReportComponent implements OnInit, OnDestroy {
    public creditorsFormControl: FormControl = new FormControl();
    public statusesFormControl: FormControl = new FormControl();
    public typesFormControl: FormControl = new FormControl();
    public fromDateFormControl: FormControl = new FormControl();
    public toDateFormControl: FormControl = new FormControl();
    public creditorsList: any = null;
    public paymentStatusesList: any = null;
    public reportData: any = [];
    public reportsListDataSource: any = [];
    public isReportPendingRetrieval: boolean = false;
    public searchParameters: any = null;

    public creditors: any | null = null;
    public selectedCreditor: any | null = null;
    public filteredItems: any[] = [];
    public pendingCancellationPaymentsRunIds: number[] = [];
    public hasClickedSearch: boolean = false;

    public constructor(
        public appService: AppService,
        public reportsService: ReportsService,
        public referenceDataService: ReferenceDataService,
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.appService.IsAppBusy = true;

        forkJoin(
            this.reportsService.getDefaultPaymentsSearchParameters(),
            this.reportsService.listAvailablePaymentsRuns(),
            this.referenceDataService.getCreditors(),
            this.referenceDataService.getPaymentStatuses(),
        ).subscribe(
            ([
                searchParameters,
                availableReports,
                creditors,
                paymentStatuses,
            ]) => {
                var allowedCreditors =
                    this.appService.CurrentSession.AuthenticatedUser.User.Claims.filter(
                        (i: any) => i.Type.toLowerCase() == 'creditorid',
                    ).map((i: any) => i.Value);
                var creditorsFiltered = creditors.filter(
                    (i: any) => allowedCreditors.indexOf(i.CreditorId) > -1,
                );
                this.reportsListDataSource = availableReports;
                this.searchParameters = searchParameters;
                this.creditorsList = creditorsFiltered;
                this.paymentStatusesList = paymentStatuses;

                this.creditors = creditors.map((i: any) => ({
                    ...i,
                    LongNameWithId: `${i.CompanyName} (${i.CreditorId})`,
                }));

                this.searchParameters.FromDate = new Date(
                    this.searchParameters.FromDate,
                );
                this.searchParameters.ToDate = new Date(
                    this.searchParameters.ToDate,
                );

                this.appService.IsAppBusy = false;

                // start the signalr listener if anything is running at the time of visiting this component
                const runningPaymentsRuns = this.reportsListDataSource.filter(
                    (r: any) => !r.FinishedOnUtc,
                );

                for (let runningPaymentsRun of runningPaymentsRuns) {
                    this.reportsService.startPaymentsProgressConnection(
                        runningPaymentsRun.PaymentsRunId,
                    );

                    this.reportsService.onPaymentsHubProgress(
                        (paymentsReportResult: any) => {
                            this.zone.run(() => {
                                this.refreshReport(paymentsReportResult);
                                this.cdr.detectChanges(); // force view updates
                            });
                        },
                    );
                }

                // detect if anything is still in the cancelation state waiting to cancel
                const pendingCancellationPaymentsRuns =
                    this.reportsListDataSource.filter(
                        (r: any) =>
                            r.CancelRequestedOnUtc != null && !r.FinishedOnUtc,
                    );

                for (let pendingCancellationPaymentsRun of pendingCancellationPaymentsRuns) {
                    const index =
                        this.pendingCancellationPaymentsRunIds.indexOf(
                            pendingCancellationPaymentsRun.PaymentsRunId,
                        );

                    if (index == -1) {
                        this.pendingCancellationPaymentsRunIds.push(
                            pendingCancellationPaymentsRun.PaymentsRunId,
                        );
                    }
                }
            },
        );
    }

    get hasTableData(): boolean {
        return isArrayWithData(this.reportsListDataSource);
    }

    public runReport = () => {
        this.hasClickedSearch = true;
        if (this.searchParameters.CreditorId == '') {
            this.appService.ShowModalDialog(
                ModalDialogSeverity.Info,
                'Missing Input',
                "You didn't enter an account contact id.  Enter an account contact id in thre format creditor/{creditorid}/accountcontact/{integrationlinkid}.  So for example creditor/1/accountcontact/abc123.",
                'GOTCHA',
                '',
            );
        } else {
            this.appService.IsAppBusy = true;

            this.reportsService
                .queuePaymentsReport(this.searchParameters)
                .subscribe((r: any | null) => {
                    if (r != null) {
                        var paymentsRunId = r.PaymentsRunId;

                        if (paymentsRunId > 0) {
                            try {
                                // setup signalr

                                this.reportsService.startPaymentsProgressConnection(
                                    paymentsRunId,
                                );

                                this.reportsService.onPaymentsHubProgress(
                                    (paymentsReportResult: any) => {
                                        this.zone.run(() => {
                                            this.refreshReport(
                                                paymentsReportResult,
                                            );
                                            this.cdr.detectChanges(); // force view updates
                                        });
                                    },
                                );
                            } catch (e) {
                                //
                            }

                            this.reportsService
                                .runPaymentsReport(paymentsRunId)
                                .subscribe((result: any | null) => {
                                    if (result != null) {
                                        this.appService.IsAppBusy = false;

                                        const index =
                                            this.pendingCancellationPaymentsRunIds.indexOf(
                                                result.PaymentsRunId,
                                            );

                                        if (index !== -1) {
                                            // this means the report finished processing as a result of a cancellation request.  remove the tracking so the gui can update appropriately

                                            this.pendingCancellationPaymentsRunIds.splice(
                                                index,
                                                1,
                                            );
                                        }

                                        this.refreshAvailableReports();
                                    }
                                });
                        }
                    }
                });

            setTimeout(() => {
                this.refreshAvailableReports();
                this.hasClickedSearch = true;
            }, 1000);
        }
    };

    public refreshAvailableReports = (): void => {
        this.reportsService
            .listAvailablePaymentsRuns()
            .subscribe((r: any | null) => {
                if (r) {
                    this.reportsListDataSource = r;
                }
            });
    };

    public refreshReport = (paymentsReportResult: any): void => {
        const index = this.reportsListDataSource.findIndex(
            (r: any) => r.PaymentsRunId === paymentsReportResult.PaymentsRunId,
        );

        if (index !== -1) {
            this.reportsListDataSource[index] = paymentsReportResult; // replace the item at the found index
            this.reportsListDataSource = [...this.reportsListDataSource]; // rebind
        }
    };

    public downloadReport(paymentsRunId: number): void {
        this.reportsService.downloadPaymentsReport(paymentsRunId).subscribe(
            (blob: any) => {
                if (blob != null) {
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');

                    a.href = downloadUrl;
                    a.download = `PaymentsReport_${paymentsRunId}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(downloadUrl);
                }
            },
            (error) => {
                console.error('Download failed', error);
            },
        );
    }

    public filterItems(event: any) {
        let filtered: any[] = [];
        let query = event.query;

        for (let i = 0; i < (this.creditors as any[]).length; i++) {
            let item = (this.creditors as any[])[i];

            if (
                item.LongNameWithId.toLowerCase().indexOf(
                    query.toLowerCase(),
                ) !== -1
            ) {
                filtered.push(item);
            }
        }

        this.filteredItems = filtered;
    }

    public onCreditorSelected(event: any): void {
        this.hasClickedSearch = false;
        this.searchParameters.CreditorId = event.value.CreditorId;
    }

    public cancelReport = (paymentsRunId: number): void => {
        this.pendingCancellationPaymentsRunIds.push(paymentsRunId);

        this.reportsService
            .requestPaymentsRunCancellation(paymentsRunId)
            .subscribe((r: any | null) => {
                if (r) {
                    const index =
                        this.pendingCancellationPaymentsRunIds.indexOf(
                            paymentsRunId,
                        );

                    if (index == -1) {
                        // incase the user clicked multiple times, avoid re-adding the tracking id and re-issuing the cancellation request

                        this.pendingCancellationPaymentsRunIds.push(
                            paymentsRunId,
                        );
                    }
                }
            });
    };

    public refreshReportsList = (): void => {
        this.refreshAvailableReports();
    };

    public ngOnDestroy(): void {
        this.reportsService.stopNotificationMetricsProgressConnection();
    }
}
